import { Certification } from '@/types/certification';
import { Course } from '@/types/course';

export interface CourseInfo {
  hasResume: boolean;
  buttonRoute: string;
  progressPercent: number;
  welcomeMessage: string;
  courseName?: string;
  courseImage?: string;
}

interface CourseWithOrder extends Course {
  categoryOrder: number; // from CourseCategory.order
  courseCategoryOrder: number; // from Course.categoryOrder
}

export function getCourseInfo(
  certs: Certification[]
): CourseInfo {
  if (certs.length === 0) {
    return {
      hasResume: false,
      buttonRoute: '/all-courses',
      progressPercent: 0,
      welcomeMessage: "Get started—your learning goals are within reach.",
      courseName: undefined,
      courseImage: undefined
    };
  }

  // Flatten all courses from all certifications with their ordering info
  const allCourses: CourseWithOrder[] = [];
  
  for (const cert of certs) {
    // Sort categories by order
    const sortedCategories = [...cert.courseCategories].sort((a, b) => a.order - b.order);
    
    for (const category of sortedCategories) {
      // Sort courses within category by categoryOrder
      const sortedCourses = [...category.courses].sort((a, b) => a.categoryOrder - b.categoryOrder);
      
      for (const course of sortedCourses) {
        allCourses.push({
          ...course,
          categoryOrder: category.order,
          courseCategoryOrder: course.categoryOrder
        });
      }
    }
  }

  // Find the first course with progress < 100
  const nextCourse = allCourses.find(course => {
    // progress can be undefined if user hasn't started, or a number 0-100+
    const progress = course.progress ?? 0;
    return progress < 100;
  });

  if (!nextCourse) {
    // All courses are completed (or no courses with progress data)
    return {
      hasResume: false,
      buttonRoute: '/all-courses',
      progressPercent: 0,
      welcomeMessage: "Get started—your learning goals are within reach.",
      courseName: undefined,
      courseImage: undefined
    };
  }

  const progress = nextCourse.progress ?? 0;
  const progressPercent = Math.min(Math.max(progress, 0), 100);

  return {
    hasResume: true,
    buttonRoute: `/course/${nextCourse.slug}`,
    progressPercent: progressPercent,
    welcomeMessage: "Keep pushing forward—your learning goals are within reach.",
    courseName: nextCourse.name,
    courseImage: nextCourse.thumbnailUrl || nextCourse.imageUrl || undefined
  };
}
